/**
 * Supplemental Instruction (SI) Type Definitions
 *
 * Frontend types for the SI tracking system.
 * Matches backend schemas in schemas/supplemental_instructions.py
 */

export type SIStatus = 'draft' | 'issued' | 'superseded';

/**
 * Status transition rules (mirrors backend VALID_TRANSITIONS).
 */
export const VALID_SI_TRANSITIONS: Record<SIStatus, SIStatus[]> = {
  draft: ['issued'],
  issued: ['superseded'],
  superseded: [],
};

/**
 * SI status display config (label + badge class)
 */
export const SI_STATUS_CONFIG: Record<SIStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-200 text-gray-800' },
  issued: { label: 'Issued', className: 'bg-blue-500 text-white' },
  superseded: { label: 'Superseded', className: 'bg-slate-600 text-white' },
};

// ============================================================================
// Request bodies
// ============================================================================

export interface SICreate {
  title: string;
  description?: string | null;
  rfi_id?: string | null;
  issued_date?: string | null;  // ISO date string
  issued_by?: string | null;
  drawing_references?: string | null;
  spec_references?: string | null;
}

export interface SIUpdate {
  title?: string;
  description?: string | null;
  issued_date?: string | null;
  issued_by?: string | null;
  drawing_references?: string | null;
  spec_references?: string | null;
}

export interface SIStatusUpdate {
  status: SIStatus;
}

export interface SendSIEmailRequest {
  recipient_emails: string[];
  custom_note?: string | null;
  reply_to_override?: string | null;
}

// ============================================================================
// Response shapes
// ============================================================================

/**
 * Complete SI object (backend → frontend)
 */
export interface SI {
  id: string;
  project_id: string;
  tenant_id: string;
  rfi_id?: string | null;
  si_number: string;   // Auto-generated e.g. "SI-001"
  title: string;
  description?: string | null;
  issued_date?: string | null;
  issued_by?: string | null;
  status: SIStatus;
  drawing_references?: string | null;
  spec_references?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListSIsResponse {
  success: boolean;
  sis: SI[];
  total: number;
  project_id: string;
}

export interface SIResponse {
  success: boolean;
  si: SI;
}

export interface SIListOptions {
  status?: SIStatus;
  search?: string;
}
